import { RegisterOptions, useForm } from 'react-hook-form';
import {
  HONEYPOT_FIELD_NAME,
  HoneypotInput,
} from 'src/components/atoms/HoneypotInput';
import {
  CareerField,
  CareerJob,
} from 'src/features/brightsaas/careers/jobsConfig';
import { useCareerApplication } from 'src/hooks/useCareerApplication';
import { TURNSTILE_ACTIONS, useTurnstile } from 'src/hooks/useTurnstile';

type CareerApplicationFormData = Record<string, string>;

type CareerApplicationFormProps = {
  job: CareerJob;
};

const isValidHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const fieldRegisterOptions = (
  field: CareerField,
): RegisterOptions<CareerApplicationFormData> => ({
  required: field.required ? 'This field is required' : false,
  maxLength: {
    value: field.maxLength,
    message: `Maximum ${field.maxLength} characters`,
  },
  ...(field.min !== undefined && {
    min: { value: field.min, message: `Must be at least ${field.min}` },
  }),
  ...(field.max !== undefined && {
    max: { value: field.max, message: `Must be at most ${field.max}` },
  }),
  ...(field.validate === 'url' && {
    validate: (value: string) =>
      !value || isValidHttpUrl(value) || 'Enter a valid URL (https://...)',
  }),
  ...(field.type === 'number' && {
    validate: (value: string) =>
      !value || /^\d+$/.test(value) || 'Enter a whole number',
  }),
});

export const CareerApplicationForm: React.FC<CareerApplicationFormProps> = ({
  job,
}) => {
  const { submitApplication, isLoading } = useCareerApplication();
  const captcha = useTurnstile(TURNSTILE_ACTIONS.CAREER);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CareerApplicationFormData>({
    defaultValues: {
      ...Object.fromEntries(job.fields.map((field) => [field.key, ''])),
      [HONEYPOT_FIELD_NAME]: '',
    },
  });

  const handleFormSubmit = async (data: CareerApplicationFormData) => {
    if (!captcha.token) return;

    const { [HONEYPOT_FIELD_NAME]: honeypot, ...fieldValues } = data;
    // Optional empty fields are omitted so the backend treats them as absent.
    const formData = Object.fromEntries(
      Object.entries(fieldValues).filter(([, value]) => value !== ''),
    );

    const result = await submitApplication({
      jobId: job.slug,
      formData,
      captchaToken: captcha.token,
      honeypot,
    });
    if (result.success) {
      reset();
    }
    // Turnstile tokens are single-use; refresh so the user can retry.
    captcha.reset();
  };

  const renderField = (field: CareerField) => {
    const error = errors[field.key];
    const label = `${field.label}${field.required ? ' *' : ''}`;
    const controlClass = `form-control form-control-xl${error ? ' is-invalid' : ''}`;

    return (
      <div key={field.key} className="col-12">
        <div className="form-group">
          <label className="form-label" htmlFor={field.key}>
            {label}
          </label>
          <div className="form-control-wrap">
            {field.type === 'short' && (
              <input
                {...register(field.key, fieldRegisterOptions(field))}
                type="text"
                id={field.key}
                className={controlClass}
                placeholder={field.placeholder}
              />
            )}

            {field.type === 'number' && (
              <input
                {...register(field.key, fieldRegisterOptions(field))}
                type="number"
                inputMode="numeric"
                id={field.key}
                className={controlClass}
                placeholder={field.placeholder}
                min={field.min}
                max={field.max}
                step={1}
              />
            )}

            {field.type === 'long' && (
              <textarea
                {...register(field.key, fieldRegisterOptions(field))}
                id={field.key}
                className={controlClass}
                placeholder={field.placeholder}
                maxLength={field.maxLength}
                rows={4}
              />
            )}

            {field.type === 'select' && (
              <select
                {...register(field.key, fieldRegisterOptions(field))}
                id={field.key}
                className={`form-select form-select-lg fs-8${error ? ' is-invalid' : ''}`}
                defaultValue="">
                <option value="" disabled>
                  Select an option
                </option>
                {field.options?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}

            {error && (
              <div className="invalid-feedback d-block">
                {error.message ?? 'Invalid value'}
              </div>
            )}

            {field.type === 'long' && (
              <div className="fs-10 text-muted text-end mt-1">
                {watch(field.key)?.length ?? 0} / {field.maxLength}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const submitLabel = isLoading
    ? 'Submitting…'
    : !captcha.ready
      ? 'Loading verification…'
      : !captcha.token
        ? 'Complete verification'
        : 'Submit Application';

  return (
    <form className="contact-form" onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="row gx-gs gy-5">
        {job.fields.map(renderField)}

        <HoneypotInput {...register(HONEYPOT_FIELD_NAME)} />

        <div className="col-12">
          <div ref={captcha.containerRef} />
        </div>

        <div className="col-12">
          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoading || !captcha.token}
              className="btn btn-primary btn-lg btn-pill">
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};
